import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip, AreaChart, Area, RadialBarChart, RadialBar, ReferenceLine,
} from "recharts";
import {
  LifeBuoy, Bell, PoundSterling, TrendingUp, ShieldCheck, ShieldAlert,
  Layers, Wallet, Activity, Trophy, ListChecks, FileSignature, SunMedium,
  Wrench, BellRing, Coins, Banknote, HeartPulse, FileClock, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { VELORY_WIDGETS, VELORY_LAYOUT, VELORY_HEIGHT } from "./velory";

/* ------------------------------------------------------------------ *
 * Formatting helpers
 * ------------------------------------------------------------------ */
const fmtGbp = (v) => v == null ? "—" : `£${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtGbpK = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1000) return `£${(n / 1000).toFixed(1)}k`;
  return `£${n.toFixed(0)}`;
};
const fmtKwh = (v) => {
  const n = Number(v || 0);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} GWh`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)} MWh`;
  return `${n.toFixed(0)} kWh`;
};
const ago = (iso) => {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (d < 1) return "today";
  if (d < 2) return "1d ago";
  if (d < 30) return `${Math.floor(d)}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
};

const C = {
  blue: "#00B3BB", amber: "#B8721E", green: "#0F8B84", red: "#B8321F",
  teal: "#314041", teal2: "#00D6DF", slate: "#7C8B8C", violet: "#B33C86",
};
const SEV = { critical: C.red, high: "#B33C86", warning: C.amber, medium: C.amber, info: C.blue, low: C.slate };

/* ------------------------------------------------------------------ *
 * Period helpers — month / quarter / year
 * ------------------------------------------------------------------ */
export const PERIODS = {
  month: { label: "This month", months: 1 },
  quarter: { label: "This quarter", months: 3 },
  year: { label: "This year", months: 12 },
};

function periodRange(key) {
  const p = PERIODS[key] || PERIODS.year;
  const today = new Date();
  const to = today.toISOString().slice(0, 10);
  let from;
  if (key === "month") from = new Date(today.getFullYear(), today.getMonth(), 1);
  else if (key === "quarter") from = new Date(today.getFullYear(), today.getMonth() - 2, 1);
  else from = new Date(today.getFullYear(), 0, 1);
  return { months: p.months, date_from: from.toISOString().slice(0, 10), date_to: to, label: p.label };
}

/* Self-fetching hook keyed on a stable url string. */
function usePeriodData(url) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    api.get(url).then(({ data }) => alive && setData(data)).catch(() => alive && setData(false));
    return () => { alive = false; };
  }, [url]);
  return data;
}

/* ------------------------------------------------------------------ *
 * Shared chrome
 * ------------------------------------------------------------------ */
function PeriodSelect({ value, onChange }) {
  return (
    <select
      value={value} onChange={(e) => onChange(e.target.value)}
      onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
      data-testid="widget-period-select"
      className="text-[10px] font-semibold rounded-md px-1.5 py-0.5 border bg-transparent cursor-pointer"
      style={{ borderColor: "var(--line)", color: "var(--ink-2)" }}
    >
      {Object.entries(PERIODS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
    </select>
  );
}

function Frame({ icon: Icon, label, color = "var(--ink)", onTitle, children, period, setPeriod, testid }) {
  return (
    <div className="h-full flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onTitle} disabled={!onTitle} data-testid="widget-title"
          className={`inline-flex items-center gap-2 min-w-0 flex-1 text-left ${onTitle ? "cursor-pointer group" : ""}`}>
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg shrink-0" style={{ background: color + "18", color }}>
            <Icon size={15} />
          </span>
          <span className="text-[10px] uppercase tracking-[0.12em] font-bold muted truncate group-hover:text-[var(--ink)]">{label}</span>
        </button>
        {setPeriod && <PeriodSelect value={period} onChange={setPeriod} />}
      </div>
      <div className="flex-1 min-h-0 flex flex-col">{children}</div>
    </div>
  );
}

function StatChart({ value, sub, color, chart, badge }) {
  return (
    <div className="flex items-end justify-between gap-2 h-full">
      <div className="flex flex-col justify-end">
        <div className="flex items-center gap-2">
          <div className="font-display text-3xl font-bold leading-none" style={{ color }}>{value}</div>
          {badge}
        </div>
        {sub && <div className="text-[11px] muted mt-1 leading-tight">{sub}</div>}
      </div>
      {chart && <div className="shrink-0" style={{ width: "48%", maxWidth: 150, height: 64 }}>{chart}</div>}
    </div>
  );
}

/* Delta badge: ▲/▼ pct vs previous period. */
function Delta({ pct }) {
  if (pct == null || !isFinite(pct)) return null;
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const col = up ? C.green : C.red;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full px-1.5 py-0.5"
      style={{ background: col + "18", color: col }}>
      <Icon size={11} />{Math.abs(pct).toFixed(0)}%
    </span>
  );
}

const ChartTip = ({ active, payload, fmt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md px-2 py-1 text-[11px] shadow-lg" style={{ background: "var(--bg)", border: "1px solid var(--line)" }}>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || p.fill }}>{p.name}: <b>{fmt ? fmt(p.value) : p.value}</b></div>
      ))}
    </div>
  );
};

/* Donut with a centered total. `data` items may carry `to` for click-through. */
function Donut({ data, size = 124, centerLabel, centerSub, onSlice }) {
  const total = data.reduce((a, d) => a + (d.value || 0), 0);
  const len = String(centerLabel ?? "").length;
  const fs = Math.round(size * (len <= 2 ? 0.2 : len <= 4 ? 0.16 : 0.13));
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={total ? data : [{ name: "none", value: 1, fill: "var(--line)" }]}
               dataKey="value" innerRadius="74%" outerRadius="100%" paddingAngle={total ? 2 : 0}
               stroke="none" startAngle={90} endAngle={-270}
               onClick={total && onSlice ? (d) => onSlice(d && d.payload ? d.payload : d) : undefined}
               style={{ cursor: total && onSlice ? "pointer" : "default", outline: "none" }}>
            {(total ? data : [{ fill: "var(--line)" }]).map((d, i) => <Cell key={i} fill={d.fill} />)}
          </Pie>
          {total > 0 && <Tooltip content={<ChartTip />} />}
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="font-display font-bold leading-none" style={{ fontSize: fs }}>{centerLabel}</span>
        {centerSub && <span className="text-[10px] muted mt-1">{centerSub}</span>}
      </div>
    </div>
  );
}

function Legend({ items, onItem }) {
  return (
    <div className="flex flex-col gap-1 justify-center">
      {items.map((it, i) => {
        const label = it.label ?? it.name ?? "";
        return (
        <button key={label || i} onClick={onItem ? () => onItem(it) : undefined} disabled={!onItem}
          className={`flex items-center gap-1.5 text-[11px] ${onItem ? "hover:opacity-70 cursor-pointer" : ""}`}>
          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: it.fill }} />
          <span className="muted truncate">{label}</span>
          <span className="font-bold ml-auto">{it.value}</span>
        </button>
        );
      })}
    </div>
  );
}

/* ================================================================== *
 * Widget catalogue
 * ================================================================== */
export const WIDGETS = [
  ...VELORY_WIDGETS,
  /* ---- OPEX tickets: donut of status split -------------------- */
  {
    id: "opex_tickets", title: "Open OPEX tickets", w: 4, h: 3, minW: 3, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const bs = s.opex?.by_status || {};
      const data = [
        { name: "Open", value: bs.open || 0, fill: C.blue, to: "/opex?view=tickets&status=open" },
        { name: "In progress", value: bs.in_progress || 0, fill: C.teal2, to: "/opex?view=tickets&status=in_progress" },
        { name: "Waiting", value: bs.waiting || 0, fill: C.amber, to: "/opex?view=tickets&status=waiting" },
      ].filter((d) => d.value > 0);
      const go = (d) => nav(d?.to || "/opex?view=tickets");
      return (
        <Frame icon={LifeBuoy} label="Open OPEX tickets" color={C.blue} onTitle={() => nav("/opex?view=tickets")} testid="w-opex-tickets">
          <div className="flex items-center gap-3 h-full">
            <Donut data={data} centerLabel={s.opex?.open_count ?? "—"} centerSub="open" onSlice={go} />
            <div className="flex-1 min-w-0">
              <Legend items={data.length ? data : [{ label: "No open tickets", value: 0, fill: "var(--line)" }]} onItem={data.length ? go : undefined} />
              {s.opex?.critical_open_count > 0 && (
                <button onClick={() => nav("/opex?view=tickets&priority=critical")} className="text-[11px] font-bold mt-1.5 hover:underline" style={{ color: C.red }}>{s.opex.critical_open_count} critical →</button>
              )}
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- New alerts -------------------------------------------- */
  {
    id: "new_alerts", title: "New alerts", w: 3, h: 2, minW: 2, minH: 2,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      return (
        <Frame icon={Bell} label="New alerts" color={C.amber} onTitle={() => nav("/opex?view=alerts")} testid="w-new-alerts">
          <StatChart value={s.opex?.new_alert_count ?? "—"} sub="Unactioned — auto-ticketed" color={C.amber} />
        </Frame>
      );
    },
  },

  /* ---- Net operating margin: income vs spend bars + period ---- */
  {
    id: "net_margin", title: "Net operating margin", w: 4, h: 3, minW: 3, minH: 3, period: true,
    Render: ({ period, setPeriod }) => {
      const nav = useNavigate();
      const r = periodRange(period);
      const data = usePeriodData(`/opex/expenditure?date_from=${r.date_from}&date_to=${r.date_to}`);
      const inc = data ? data.total_income_gbp : null;
      const out = data ? data.total_spend_gbp : null;
      const m = data ? data.total_net_margin_gbp : null;
      const chart = (
        <ResponsiveContainer>
          <BarChart data={[{ name: "In", v: inc || 0 }, { name: "Out", v: out || 0 }]} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
                    onClick={() => nav("/opex?view=expenditure")} style={{ cursor: "pointer" }}>
            <defs>
              <linearGradient id="nmIn" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.green} stopOpacity={1} /><stop offset="100%" stopColor={C.green} stopOpacity={0.55} />
              </linearGradient>
              <linearGradient id="nmOut" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.red} stopOpacity={1} /><stop offset="100%" stopColor={C.red} stopOpacity={0.55} />
              </linearGradient>
            </defs>
            <XAxis dataKey="name" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTip fmt={fmtGbp} />} cursor={{ fill: "var(--line)", opacity: 0.3 }} />
            <Bar dataKey="v" name="Amount" radius={[4, 4, 0, 0]}>
              {["url(#nmIn)", "url(#nmOut)"].map((c, i) => <Cell key={i} fill={c} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
      return (
        <Frame icon={PoundSterling} label="Net operating margin" color={m >= 0 ? C.green : C.red}
               onTitle={() => nav("/opex?view=expenditure")} period={period} setPeriod={setPeriod} testid="w-net-margin">
          <StatChart value={data === false ? "—" : fmtGbp(m)}
                     sub={data ? `in ${fmtGbpK(inc)} · out ${fmtGbpK(out)}` : "Loading…"}
                     color={m >= 0 ? C.green : C.red} chart={chart} />
        </Frame>
      );
    },
  },

  /* ---- Portfolio IRR: radial gauge ---------------------------- */
  {
    id: "portfolio_irr", title: "Portfolio IRR", w: 3, h: 3, minW: 3, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const irr = s.irr?.avg_projected_irr_pct;
      const pct = Math.max(0, Math.min(100, (irr || 0) * 4));
      return (
        <Frame icon={TrendingUp} label="Portfolio IRR" color={C.teal} onTitle={() => nav("/pm/investor")} testid="w-portfolio-irr">
          <div className="flex items-center gap-2 h-full">
            <div className="relative shrink-0" style={{ width: 124, height: 124 }}>
              <ResponsiveContainer>
                <RadialBarChart innerRadius="78%" outerRadius="100%" barSize={10}
                                data={[{ v: pct, fill: C.teal }]} startAngle={90} endAngle={-270}>
                  <RadialBar background={{ fill: "var(--line)" }} dataKey="v" cornerRadius={6} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display text-xl font-bold leading-none" style={{ color: C.teal }}>{irr != null ? `${Number(irr).toFixed(1)}%` : "—"}</span>
                <span className="text-[9px] muted">avg IRR</span>
              </div>
            </div>
            <div className="text-[11px] muted">
              {s.irr ? <><b style={{ color: C.red }}>{s.irr.below_floor_count}</b> site(s) below {s.irr.floor_pct}% floor</> : "—"}
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- Warranty status: donut -------------------------------- */
  {
    id: "warranty_expiring", title: "Warranty status", w: 4, h: 3, minW: 3, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const st = s.assets?.by_status || {};
      const soon = st.expiring_soon?.count || 0;
      const exp = st.expired?.count || 0;
      const inw = st.in_warranty?.count || 0;
      const data = [
        { name: "Expiring soon", value: soon, fill: C.amber },
        { name: "Expired", value: exp, fill: C.red },
        { name: "In warranty", value: inw, fill: C.green },
      ].filter((d) => d.value > 0);
      const go = () => nav("/assets?view=board");
      return (
        <Frame icon={ShieldAlert} label="Warranty status" color={C.amber} onTitle={go} testid="w-warranty-expiring">
          <div className="flex items-center gap-3 h-full">
            <Donut data={data} centerLabel={soon + exp} centerSub="at risk" onSlice={go} />
            <div className="flex-1 min-w-0"><Legend items={data.length ? data : [{ label: "No assets", value: 0, fill: "var(--line)" }]} onItem={data.length ? go : undefined} /></div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- Assets in warranty: donut proportion ------------------- */
  {
    id: "assets_in_warranty", title: "Assets in warranty", w: 3, h: 3, minW: 2, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const inw = s.assets?.by_status?.in_warranty?.count || 0;
      const total = s.assets?.total_assets || 0;
      const other = Math.max(0, total - inw);
      const data = [{ name: "In warranty", value: inw, fill: C.green }, { name: "Other", value: other, fill: "var(--line)" }];
      const pct = total ? Math.round((inw / total) * 100) : 0;
      const go = () => nav("/assets?view=register");
      return (
        <Frame icon={ShieldCheck} label="Assets in warranty" color={C.green} onTitle={go} testid="w-assets-warranty">
          <div className="flex items-center gap-3 h-full">
            <Donut data={data} centerLabel={`${pct}%`} centerSub="covered" onSlice={go} />
            <div className="text-[11px] muted leading-tight">
              <div className="font-display text-xl font-bold" style={{ color: C.green }}>{inw}</div>
              <div>of {total} assets</div>
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- Recorded income (area sparkline) + period -------------- */
  {
    id: "income_month", title: "Recorded income", w: 4, h: 3, minW: 3, minH: 3, period: true,
    Render: ({ period, setPeriod }) => {
      const nav = useNavigate();
      const data = usePeriodData(`/opex/margin-trend?months=${periodRange(period).months}`);
      const series = data?.series || [];
      const total = series.reduce((a, r) => a + (r.income_gbp || 0), 0);
      const last = series[series.length - 1];
      const prev = series[series.length - 2];
      const delta = last && prev && prev.income_gbp ? ((last.income_gbp - prev.income_gbp) / prev.income_gbp) * 100 : null;
      const chart = (
        <ResponsiveContainer>
          <AreaChart data={series} margin={{ top: 4, right: 2, left: 2, bottom: 0 }} onClick={() => nav("/income")} style={{ cursor: "pointer" }}>
            <defs>
              <linearGradient id="incFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.green} stopOpacity={0.5} />
                <stop offset="100%" stopColor={C.green} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip content={<ChartTip fmt={fmtGbp} />} labelFormatter={() => ""} />
            <Area type="monotone" dataKey="income_gbp" name="Income" stroke={C.green} strokeWidth={2} fill="url(#incFill)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      );
      return (
        <Frame icon={Wallet} label="Recorded income" color={C.green} onTitle={() => nav("/income")}
               period={period} setPeriod={setPeriod} testid="w-income-month">
          <StatChart value={data === false ? "—" : fmtGbp(period === "month" ? last?.income_gbp : total)}
                     sub={period === "month" ? (last?.month || "this month") : PERIODS[period].label}
                     color={C.green} chart={chart}
                     badge={period === "month" ? <Delta pct={delta} /> : null} />
        </Frame>
      );
    },
  },

  /* ---- NEW: Income mix by stream (donut) + period ------------- */
  {
    id: "income_streams", title: "Income by stream", w: 4, h: 3, minW: 3, minH: 3, period: true,
    Render: ({ period, setPeriod }) => {
      const nav = useNavigate();
      const data = usePeriodData(`/dashboard/income-streams?months=${periodRange(period).months}`);
      const colors = { billing: C.green, export: C.teal2, rental: C.violet, fit: C.amber };
      const rows = (data?.streams || []).map((x) => ({ name: x.label, value: Math.round(x.gbp), fill: colors[x.key] })).filter((x) => x.value > 0);
      const go = () => nav("/income");
      return (
        <Frame icon={Coins} label="Income by stream" color={C.green} onTitle={go}
               period={period} setPeriod={setPeriod} testid="w-income-streams">
          <div className="flex items-center gap-3 h-full">
            <Donut data={rows} centerLabel={data === false ? "—" : fmtGbpK(data?.total_gbp)} centerSub="total" onSlice={go} />
            <div className="flex-1 min-w-0">
              <Legend items={rows.length ? rows.map((r) => ({ label: r.name, fill: r.fill, value: fmtGbpK(r.value) })) : [{ label: "No income", value: 0, fill: "var(--line)" }]} onItem={rows.length ? go : undefined} />
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- Meter-drift events: monthly bar count ------------------ */
  {
    id: "drift_events", title: "Meter-drift events", w: 4, h: 3, minW: 3, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const events = s.drift?.events || [];
      const byMonth = {};
      events.forEach((e) => { const k = (e.created_at || "").slice(0, 7); if (k) byMonth[k] = (byMonth[k] || 0) + 1; });
      const data = Object.keys(byMonth).sort().slice(-6).map((k) => ({ month: k.slice(5), count: byMonth[k] }));
      const chart = (
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }} onClick={() => nav("/opex?view=alerts")} style={{ cursor: "pointer" }}>
            <XAxis dataKey="month" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTip />} cursor={{ fill: "var(--line)", opacity: 0.3 }} />
            <Bar dataKey="count" name="Events" fill={C.amber} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
      return (
        <Frame icon={Activity} label="Meter-drift events" color={C.amber} onTitle={() => nav("/opex?view=alerts")} testid="w-drift-events">
          <StatChart value={s.drift?.count ?? "—"} sub="Logged meter faults" color={C.amber} chart={data.length ? chart : null} />
        </Frame>
      );
    },
  },

  /* ---- Tasks due (donut of urgency + list) -------------------- */
  {
    id: "tasks_due", title: "Tasks due", w: 4, h: 3, minW: 3, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const t = s.tasks;
      const data = [
        { name: "Overdue", value: t?.overdue || 0, fill: C.red },
        { name: "Due ≤7d", value: t?.due_soon || 0, fill: C.amber },
        { name: "Upcoming", value: t?.upcoming || 0, fill: C.blue },
      ].filter((d) => d.value > 0);
      const go = () => nav("/pm");
      return (
        <Frame icon={ListChecks} label="Install tasks due" color={C.blue} onTitle={go} testid="w-tasks-due">
          <div className="flex items-center gap-3 h-full">
            <Donut data={data} centerLabel={t?.open_total ?? "—"} centerSub="open" onSlice={go} />
            <div className="flex-1 min-w-0 overflow-hidden">
              <Legend items={data.length ? data : [{ label: "Nothing due", value: 0, fill: "var(--line)" }]} onItem={data.length ? go : undefined} />
              {(t?.items || []).slice(0, 2).map((it) => (
                <div key={it.id} className="text-[10px] muted truncate mt-1">
                  <span style={{ color: it.days < 0 ? C.red : C.amber }}>{it.days < 0 ? `${-it.days}d late` : `${it.days}d`}</span> · {it.name}
                </div>
              ))}
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- NEW: Upcoming maintenance visits (donut + next) -------- */
  {
    id: "maintenance_upcoming", title: "Upcoming maintenance", w: 4, h: 3, minW: 3, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const visits = s.maint || [];
      const today = new Date();
      let overdue = 0, wk = 0, mo = 0, later = 0;
      visits.forEach((v) => {
        const d = (new Date(v.scheduled_date) - today) / 86400000;
        if (d < 0) overdue++; else if (d <= 7) wk++; else if (d <= 30) mo++; else later++;
      });
      const data = [
        { name: "Overdue", value: overdue, fill: C.red },
        { name: "≤7 days", value: wk, fill: C.amber },
        { name: "≤30 days", value: mo, fill: C.blue },
        { name: "Later", value: later, fill: C.slate },
      ].filter((d) => d.value > 0);
      const go = () => nav("/operations");
      return (
        <Frame icon={Wrench} label="Upcoming maintenance" color={C.blue} onTitle={go} testid="w-maintenance-upcoming">
          <div className="flex items-center gap-3 h-full">
            <Donut data={data} centerLabel={visits.length} centerSub="visits" onSlice={go} />
            <div className="flex-1 min-w-0 overflow-hidden">
              <Legend items={data.length ? data : [{ label: "None scheduled", value: 0, fill: "var(--line)" }]} onItem={data.length ? go : undefined} />
              {visits.slice(0, 2).map((v) => (
                <div key={v.id} className="text-[10px] muted truncate mt-1">{(v.scheduled_date || "").slice(5)} · {v.project_name}</div>
              ))}
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- NEW: Alerts feed (recent list) ------------------------- */
  {
    id: "alerts_feed", title: "Alerts feed", w: 4, h: 4, minW: 3, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const alerts = s.alertsFeed?.alerts || [];
      return (
        <Frame icon={BellRing} label="Recent alerts" color={C.red} onTitle={() => nav("/opex?view=alerts")} testid="w-alerts-feed">
          {alerts.length === 0 ? (
            <div className="muted text-xs flex items-center justify-center h-full">{s.alertsFeed === undefined ? "Loading…" : "No alerts"}</div>
          ) : (
            <div className="space-y-1.5 overflow-auto -mr-1 pr-1">
              {alerts.slice(0, 8).map((a) => (
                <button key={a.id} onClick={() => nav("/opex?view=alerts")} className="w-full text-left flex items-start gap-2 text-[11px] hover:opacity-70">
                  <span className="w-2 h-2 rounded-full mt-1 shrink-0" style={{ background: SEV[a.severity] || C.slate }} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{a.title || a.message || "Alert"}</span>
                    <span className="muted text-[10px]">{a.project_name || "Portfolio"} · {ago(a.received_at)}</span>
                  </span>
                  {a.status === "new" && <span className="text-[8px] font-bold px-1 py-0.5 rounded" style={{ background: C.red + "18", color: C.red }}>NEW</span>}
                </button>
              ))}
            </div>
          )}
        </Frame>
      );
    },
  },

  /* ---- NEW: Awaiting sign-off (horizontal bars) --------------- */
  {
    id: "signoffs", title: "Awaiting sign-off", w: 4, h: 3, minW: 3, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const d = s.signoffs;
      const data = (d?.by_department || []).map((x, i) => ({
        name: x.label, count: x.count, fill: [C.violet, C.blue, C.teal, C.amber, C.slate][i % 5],
      }));
      return (
        <Frame icon={FileSignature} label="Awaiting sign-off" color={C.violet} onTitle={() => nav("/pm")} testid="w-signoffs">
          <div className="flex items-center gap-2 h-full">
            <button onClick={() => nav("/pm")} className="flex flex-col justify-center text-left hover:opacity-70">
              <div className="font-display text-3xl font-bold leading-none" style={{ color: C.violet }}>{d?.pending ?? "—"}</div>
              <div className="text-[10px] muted mt-1">pending approvals</div>
            </button>
            <div className="flex-1 min-w-0" style={{ height: 80 }}>
              {data.length ? (
                <ResponsiveContainer>
                  <BarChart layout="vertical" data={data} margin={{ top: 0, right: 6, left: 0, bottom: 0 }}
                            onClick={() => nav("/pm")} style={{ cursor: "pointer" }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip />} cursor={{ fill: "var(--line)", opacity: 0.3 }} />
                    <Bar dataKey="count" name="Pending" radius={[0, 3, 3, 0]} barSize={11}>
                      {data.map((x, i) => <Cell key={i} fill={x.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="muted text-xs flex items-center h-full">All signed off</div>}
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- Generation chart (actual vs expected) + period --------- */
  {
    id: "generation", title: "Portfolio generation", w: 6, h: 3, minW: 4, minH: 3, period: true,
    Render: ({ period, setPeriod }) => {
      const nav = useNavigate();
      const data = usePeriodData(`/monitoring/portfolio?group=monthly`);
      const n = periodRange(period).months;
      const series = (data?.samples || []).slice(-n).map((r) => ({
        month: (r.date || "").slice(2), actual: r.actual_kwh, expected: r.simulated_kwh,
      }));
      const totalActual = series.reduce((a, r) => a + (r.actual || 0), 0);
      return (
        <Frame icon={SunMedium} label="Portfolio generation" color={C.amber} onTitle={() => nav("/analytics")}
               period={period} setPeriod={setPeriod} testid="w-generation">
          <div className="flex items-baseline gap-3 mb-1">
            <span className="font-display text-2xl font-bold" style={{ color: C.amber }}>{data === false ? "—" : fmtKwh(totalActual)}</span>
            <span className="text-[10px] muted">generated · {PERIODS[period].label.toLowerCase()}</span>
          </div>
          <div className="flex-1 min-h-0">
            {series.length ? (
              <ResponsiveContainer>
                <AreaChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} onClick={() => nav("/analytics")} style={{ cursor: "pointer" }}>
                  <defs>
                    <linearGradient id="genFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={C.amber} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={C.amber} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip fmt={fmtKwh} />} />
                  <Area type="monotone" dataKey="expected" name="Expected" stroke={C.slate} strokeWidth={1.5} strokeDasharray="3 3" fill="none" dot={false} />
                  <Area type="monotone" dataKey="actual" name="Actual" stroke={C.amber} strokeWidth={2} fill="url(#genFill)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="muted text-xs flex items-center h-full">No generation data</div>}
          </div>
        </Frame>
      );
    },
  },

  /* ---- NEW: Cash flow (cumulative net, gradient area) + period */
  {
    id: "cash_flow", title: "Cash flow", w: 5, h: 3, minW: 4, minH: 3, period: true,
    Render: ({ period, setPeriod }) => {
      const nav = useNavigate();
      const data = usePeriodData(`/opex/margin-trend?months=${Math.max(6, periodRange(period).months)}`);
      let running = 0;
      const series = (data?.series || []).map((r) => {
        running += r.net_margin_gbp || 0;
        return { month: (r.month || "").slice(2), cum: Math.round(running), net: r.net_margin_gbp };
      });
      const end = series.length ? series[series.length - 1].cum : null;
      const col = (end ?? 0) >= 0 ? C.green : C.red;
      return (
        <Frame icon={Banknote} label="Cumulative cash flow" color={col} onTitle={() => nav("/income")}
               period={period} setPeriod={setPeriod} testid="w-cash-flow">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-display text-2xl font-bold" style={{ color: col }}>{data === false ? "—" : fmtGbp(end)}</span>
            <span className="text-[10px] muted">net to date</span>
          </div>
          <div className="flex-1 min-h-0">
            {series.length ? (
              <ResponsiveContainer>
                <AreaChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} onClick={() => nav("/income")} style={{ cursor: "pointer" }}>
                  <defs>
                    <linearGradient id="cfFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={col} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={col} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9 }} width={38} tickFormatter={fmtGbpK} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip fmt={fmtGbp} />} />
                  <ReferenceLine y={0} stroke="var(--ink-2)" strokeDasharray="2 2" />
                  <Area type="monotone" dataKey="cum" name="Cumulative" stroke={col} strokeWidth={2} fill="url(#cfFill)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="muted text-xs flex items-center h-full">No cash-flow data</div>}
          </div>
        </Frame>
      );
    },
  },

  /* ---- NEW: Documents / certificates expiring ---------------- */
  {
    id: "docs_expiring", title: "Certificates expiring", w: 4, h: 3, minW: 3, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const d = s.docs;
      const data = [
        { name: "Expiring soon", value: d?.expiring_soon || 0, fill: C.amber },
        { name: "Expired", value: d?.expired || 0, fill: C.red },
        { name: "In cover", value: d?.in_warranty || 0, fill: C.green },
      ].filter((x) => x.value > 0);
      const go = () => nav("/assets?view=report");
      return (
        <Frame icon={FileClock} label="Certificates expiring" color={C.amber} onTitle={go} testid="w-docs-expiring">
          <div className="flex items-center gap-3 h-full">
            <Donut data={data} centerLabel={(d?.expiring_soon || 0) + (d?.expired || 0)} centerSub="at risk" onSlice={go} />
            <div className="flex-1 min-w-0 overflow-hidden">
              <Legend items={data.length ? data : [{ label: "No certificates", value: 0, fill: "var(--line)" }]} onItem={data.length ? go : undefined} />
              {(d?.items || []).slice(0, 2).map((it) => (
                <div key={it.id} className="text-[10px] muted truncate mt-1">
                  <span style={{ color: it.status === "expired" ? C.red : C.amber }}>●</span> {it.name} · {it.project_name}
                </div>
              ))}
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- NEW: Per-site health score ---------------------------- */
  {
    id: "site_health", title: "Site health", w: 5, h: 4, minW: 4, minH: 3,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      const h = s.health;
      const avg = h?.avg_score;
      const bands = h?.bands || { healthy: 0, watch: 0, at_risk: 0 };
      const col = avg == null ? C.slate : (avg >= 80 ? C.green : avg >= 60 ? C.amber : C.red);
      const bandCol = { healthy: C.green, watch: C.amber, at_risk: C.red };
      const worst = (h?.sites || []).slice(0, 4);
      return (
        <Frame icon={HeartPulse} label="Portfolio site health" color={col} onTitle={() => nav("/pm/investor")} testid="w-site-health">
          <div className="flex items-center gap-3 h-full">
            <div className="relative shrink-0" style={{ width: 124, height: 124 }}>
              <ResponsiveContainer>
                <RadialBarChart innerRadius="78%" outerRadius="100%" barSize={10}
                                data={[{ v: avg || 0, fill: col }]} startAngle={90} endAngle={-270}>
                  <RadialBar background={{ fill: "var(--line)" }} dataKey="v" cornerRadius={6} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display text-2xl font-bold leading-none" style={{ color: col }}>{avg ?? "—"}</span>
                <span className="text-[9px] muted">avg score</span>
              </div>
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="flex gap-2 mb-1.5">
                {Object.entries(bands).map(([k, v]) => (
                  <span key={k} className="text-[10px] font-bold inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: bandCol[k] }} />{v}
                  </span>
                ))}
              </div>
              {worst.length ? worst.map((r) => (
                <button key={r.project_id} onClick={() => nav(`/projects/${r.project_id}`)}
                  className="w-full flex items-center justify-between gap-2 text-[11px] hover:opacity-70 mb-0.5">
                  <span className="truncate">{r.project_name}</span>
                  <span className="font-mono font-bold px-1.5 rounded shrink-0" style={{ background: bandCol[r.band] + "18", color: bandCol[r.band] }}>{r.score}</span>
                </button>
              )) : <div className="muted text-xs">No scored sites</div>}
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- Pipeline: capacity + projects -------------------------- */
  {
    id: "pipeline_value", title: "Pipeline", w: 4, h: 3, minW: 3, minH: 2,
    Render: ({ sources: s }) => {
      const nav = useNavigate();
      return (
        <Frame icon={Layers} label="Pipeline" color={C.blue} onTitle={() => nav("/pm/investor")} testid="w-pipeline">
          <div className="flex items-center gap-6 h-full">
            <div>
              <div className="font-display text-3xl font-bold" style={{ color: C.blue }}>
                {Number(s.pipeline?.portfolio_total_kwp || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div className="text-[10px] muted">kWp capacity</div>
            </div>
            <div>
              <div className="font-display text-3xl font-bold">{s.pipeline?.portfolio_total_projects ?? "—"}</div>
              <div className="text-[10px] muted">projects</div>
            </div>
          </div>
        </Frame>
      );
    },
  },

  /* ---- Top sites by margin: horizontal bars + period ---------- */
  {
    id: "top_margin", title: "Top sites by margin", w: 5, h: 4, minW: 4, minH: 3, period: true,
    Render: ({ period, setPeriod }) => {
      const nav = useNavigate();
      const data = usePeriodData(`/opex/margin-leaderboard?months=${periodRange(period).months}`);
      const rows = (data?.sites || []).slice(0, 6).map((r) => ({
        name: r.project_name, margin: r.net_margin_gbp, pid: r.project_id,
        fill: r.net_margin_gbp >= 0 ? C.green : C.red,
      }));
      return (
        <Frame icon={Trophy} label="Top sites by net margin" color={C.green} onTitle={() => nav("/pm/investor")}
               period={period} setPeriod={setPeriod} testid="w-top-margin">
          {rows.length ? (
            <ResponsiveContainer>
              <BarChart layout="vertical" data={rows} margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
                <XAxis type="number" tickFormatter={fmtGbpK} tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v && v.length > 16 ? `${v.slice(0, 15)}…` : v)} />
                <Tooltip content={<ChartTip fmt={fmtGbp} />} cursor={{ fill: "var(--line)", opacity: 0.3 }} />
                <Bar dataKey="margin" name="Net margin" radius={[0, 3, 3, 0]} barSize={14}
                     onClick={(d) => d?.pid && nav(`/projects/${d.pid}`)} style={{ cursor: "pointer" }}>
                  {rows.map((r, i) => <Cell key={i} fill={r.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="muted text-sm flex items-center justify-center h-full">{data === false ? "Unavailable" : "No margin data"}</div>}
        </Frame>
      );
    },
  },
];

export const WIDGET_MAP = Object.fromEntries(WIDGETS.map((w) => [w.id, w]));

/* Default arrangement for a fresh user (react-grid-layout items). */
const LEGACY_LAYOUT = [
  { i: "opex_tickets", x: 0, y: 0, w: 4, h: 3 },
  { i: "net_margin", x: 4, y: 0, w: 4, h: 3 },
  { i: "portfolio_irr", x: 8, y: 0, w: 4, h: 3 },
  { i: "generation", x: 0, y: 3, w: 6, h: 3 },
  { i: "income_month", x: 6, y: 3, w: 4, h: 3 },
  { i: "new_alerts", x: 10, y: 3, w: 2, h: 3 },
  { i: "warranty_expiring", x: 0, y: 6, w: 4, h: 3 },
  { i: "assets_in_warranty", x: 4, y: 6, w: 3, h: 3 },
  { i: "tasks_due", x: 7, y: 6, w: 5, h: 3 },
  { i: "top_margin", x: 0, y: 9, w: 5, h: 4 },
  { i: "alerts_feed", x: 5, y: 9, w: 4, h: 4 },
  { i: "maintenance_upcoming", x: 9, y: 9, w: 3, h: 3 },
];
export const DEFAULT_LAYOUT = [...VELORY_LAYOUT, ...LEGACY_LAYOUT.map((l) => ({ ...l, y: l.y + VELORY_HEIGHT }))];
export const DEFAULT_WIDGETS = DEFAULT_LAYOUT.map((l) => l.i);
export const DEFAULT_PERIOD = "year";
